import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { LoadingSpinner } from '../../../components/layout/SharedComponents';

export function InvoiceSettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({ enabled: false, approval_enabled: false, matching_tolerance_pct: 2, allow_non_po_invoices: true, allow_additional_invoices_on_fully_matched: true, ocr_cost_per_scan: 0.25 });
  const [ingestConfig, setIngestConfig] = useState({ enabled: false, ingest_email: '', auto_ocr: true, auto_match_po: true, notify_on_error: true });
  const [savingIngest, setSavingIngest] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/api/invoices/settings'),
      api.get('/api/invoices/email-ingest/config').catch(() => ({ data: {} })),
    ]).then(([settingsRes, ingestRes]) => {
      setSettings(settingsRes.data);
      if (ingestRes.data) setIngestConfig(ic => ({ ...ic, ...ingestRes.data }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/invoices/settings', settings);
      toast.success('Invoice settings saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const saveIngest = async () => {
    setSavingIngest(true);
    try {
      await api.put('/api/invoices/email-ingest/config', ingestConfig);
      toast.success('Email ingest settings saved');
    } catch { toast.error('Save failed'); }
    setSavingIngest(false);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">Invoices</h1>
      <p className="text-lg font-medium text-muted-foreground mb-6">Invoice Module Settings</p>

      <div className="space-y-5 max-w-xl">
        <div className="flex items-center justify-between p-4 border rounded-xl">
          <div>
            <p className="font-semibold text-sm">Enable Invoice Module</p>
            <p className="text-xs text-muted-foreground">Allow invoices to be entered and processed in the system.</p>
          </div>
          <Switch checked={settings.enabled} onCheckedChange={v => setSettings(s => ({ ...s, enabled: v }))} />
        </div>

        {settings.enabled && (
          <>
            <div className="flex items-center justify-between p-4 border rounded-xl">
              <div>
                <p className="font-semibold text-sm">Invoice Approval Workflow</p>
                <p className="text-xs text-muted-foreground">When enabled, invoices must go through an approval process before payment. Configure approval rules separately.</p>
              </div>
              <Switch checked={settings.approval_enabled} onCheckedChange={v => setSettings(s => ({ ...s, approval_enabled: v }))} />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-xl">
              <div>
                <p className="font-semibold text-sm">Allow Non-PO Invoices</p>
                <p className="text-xs text-muted-foreground">Allow invoices to be entered without a matching purchase order. A ghost PO will be created automatically.</p>
              </div>
              <Switch checked={settings.allow_non_po_invoices} onCheckedChange={v => setSettings(s => ({ ...s, allow_non_po_invoices: v }))} />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-xl">
              <div>
                <p className="font-semibold text-sm">Allow Additional Invoices on Fully Matched POs</p>
                <p className="text-xs text-muted-foreground">When enabled, users can log additional invoices even after a purchase order is fully matched. They will receive a warning but can proceed. When disabled, Log Invoice and OCR buttons are locked once a PO is fully matched.</p>
              </div>
              <Switch checked={settings.allow_additional_invoices_on_fully_matched} onCheckedChange={v => setSettings(s => ({ ...s, allow_additional_invoices_on_fully_matched: v }))} />
            </div>

            <div className="p-4 border rounded-xl space-y-3">
              <p className="font-semibold text-sm">PO-to-Invoice Matching Tolerance</p>
              <p className="text-xs text-muted-foreground">Percentage variance allowed between PO and invoice amounts before flagging a mismatch.</p>
              <div className="flex items-center gap-2">
                <Input type="number" step="0.5" min="0" max="100" value={settings.matching_tolerance_pct}
                  onChange={e => setSettings(s => ({ ...s, matching_tolerance_pct: parseFloat(e.target.value) || 0 }))}
                  className="h-8 w-24 text-sm" />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            </div>

            <div className="p-4 border rounded-xl space-y-3">
              <p className="font-semibold text-sm">OCR Invoice Scan Cost</p>
              <p className="text-xs text-muted-foreground">Fixed cost charged per OCR invoice scan (manual upload or email ingest). This is a per-tenant billing cost.</p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">$</span>
                <Input type="number" step="0.01" min="0" value={settings.ocr_cost_per_scan}
                  onChange={e => setSettings(s => ({ ...s, ocr_cost_per_scan: parseFloat(e.target.value) || 0 }))}
                  className="h-8 w-24 text-sm" />
                <span className="text-sm text-muted-foreground">per scan</span>
              </div>
            </div>
          </>
        )}

        <div className="flex justify-start">
          <Button onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</Button>
        </div>

        {/* Email Ingest Section */}
        {settings.enabled && (
          <div className="border-t pt-5 mt-5">
            <h2 className="text-lg font-bold mb-1">Email Ingest</h2>
            <p className="text-sm text-muted-foreground mb-4">Configure automatic invoice processing from supplier emails.</p>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-xl">
                <div>
                  <p className="font-semibold text-sm">Enable Email Ingest</p>
                  <p className="text-xs text-muted-foreground">Allow suppliers to email invoices to a dedicated address for automatic OCR processing.</p>
                </div>
                <Switch checked={ingestConfig.enabled} onCheckedChange={v => setIngestConfig(s => ({ ...s, enabled: v }))} />
              </div>

              {ingestConfig.enabled && (
                <>
                  <div className="p-4 border rounded-xl space-y-3">
                    <p className="font-semibold text-sm">Ingest Email Address</p>
                    <p className="text-xs text-muted-foreground">Suppliers will send invoices to this email address. Set up SendGrid Inbound Parse (or similar) to forward emails to the webhook endpoint shown below.</p>
                    <Input value={ingestConfig.ingest_email} onChange={e => setIngestConfig(s => ({ ...s, ingest_email: e.target.value }))}
                      className="h-8 text-sm" placeholder="e.g. invoices@yourdomain.com" />
                    <div className="bg-muted/50 rounded-lg px-3 py-2 text-xs space-y-1">
                      <p className="font-semibold">Webhook Endpoint (for mail forwarding service):</p>
                      <code className="block text-primary break-all">{process.env.REACT_APP_BACKEND_URL}/api/invoices/email-ingest/receive</code>
                      <p className="text-muted-foreground mt-1">In SendGrid: Settings &gt; Inbound Parse &gt; Add Host &amp; URL. Point your MX record to <code>mx.sendgrid.net</code> and set the POST URL to the endpoint above.</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-xl">
                    <div>
                      <p className="font-semibold text-sm">Auto-Process with OCR</p>
                      <p className="text-xs text-muted-foreground">Automatically run OCR on incoming invoice attachments. If disabled, invoices are queued for manual processing.</p>
                    </div>
                    <Switch checked={ingestConfig.auto_ocr} onCheckedChange={v => setIngestConfig(s => ({ ...s, auto_ocr: v }))} />
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-xl">
                    <div>
                      <p className="font-semibold text-sm">Auto-Match PO & Create Invoice</p>
                      <p className="text-xs text-muted-foreground">When OCR finds a PO number, automatically create the invoice and match it. Invoices without a PO match are flagged for review.</p>
                    </div>
                    <Switch checked={ingestConfig.auto_match_po} onCheckedChange={v => setIngestConfig(s => ({ ...s, auto_match_po: v }))} />
                  </div>

                  <div className="flex items-center justify-between p-4 border rounded-xl">
                    <div>
                      <p className="font-semibold text-sm">Notify on Error</p>
                      <p className="text-xs text-muted-foreground">Send notifications to administrators when an email ingest fails or requires human intervention.</p>
                    </div>
                    <Switch checked={ingestConfig.notify_on_error} onCheckedChange={v => setIngestConfig(s => ({ ...s, notify_on_error: v }))} />
                  </div>
                </>
              )}

              <div className="flex justify-start">
                <Button onClick={saveIngest} disabled={savingIngest}>{savingIngest ? 'Saving...' : 'Save Email Ingest Settings'}</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
